import { useMemo, useContext } from 'react';
import { TableContext } from '../../shared/table/context/TableContext';
import { Link } from 'react-router-dom';
import { formatDate } from '../../../utils/dateUtils';
import { useAuth } from '../../../context/AuthContext';

const renderCellSafely = (row, field, type, renderer) => {
  try {
    const value = row[field];
    if (value === null || value === undefined) {
      return '-';
    }
    
    let typedValue;
    switch (type) {
      case 'number':
        typedValue = isNaN(Number(value)) ? null : Number(value);
        break;
      case 'string':
        typedValue = String(value);
        break;
      default:
        typedValue = value;
    }
    
    if (typedValue === null) {
      return '-';
    }
    
    return renderer(row, typedValue);
  } catch (error) {
    console.error('Error rendering cell:', error);
    return '-';
  }
};

export const useColumns = (transactions = [], type = 'sales') => {
  const { preferences: { columnOrder = [] } } = useContext(TableContext);
  const { user } = useAuth();

  return useMemo(() => {
    if (!transactions.length) {
      return [];
    }

    // Define base columns that appear in both sales and purchases
    const baseColumns = [
      {
        field: 'id',
        headerName: 'ID',
        type: 'number',
        sortable: true,
        sortComparator: (a, b) => Number(a) - Number(b),
        renderCell: (row) => renderCellSafely(row, 'id', 'number',
          (r, value) => value
        )
      },
      {
        field: 'transaction_date',
        headerName: 'Date',
        type: 'string',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'transaction_date', 'string',
          (r, value) => formatDate(value, { user })
        )
      },
      {
        field: 'coins_description',
        headerName: 'Coin',
        type: 'string',
        sortable: false,
        renderCell: (row) => (
          <button 
            className="text-left hover:bg-gray-50 w-full p-2 -m-2 rounded"
          >
            <div className="text-blue-600 hover:text-blue-800">
              {row.coins_description}
            </div>
          </button>
        )
      }
    ];

    // Add type-specific columns
    const typeSpecificColumns = type === 'sales' ? [
      {
        field: 'sale_price',
        headerName: 'Sale Price',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'sale_price', 'number',
          (r, value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        )
      },
      {
        field: 'fees',
        headerName: 'Fees',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'fees', 'number',
          (r, value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`
        )
      },
      {
        field: 'net_amount',
        headerName: 'Net Sale',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'net_amount', 'number',
          (r, value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        )
      },
      {
        field: 'total_cost',
        headerName: 'Total Cost',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'total_cost', 'number',
          (r, value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        )
      },
      {
        field: 'profit',
        headerName: 'Profit',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'profit', 'number',
          (r, value) => `$${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
        )
      },
      {
        field: 'margin_percentage',
        headerName: 'Margin',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'margin_percentage', 'number',
          (r, value) => `${value}%`
        )
      }
    ] : [
      {
        field: 'purchase_price',
        headerName: 'Purchase Price',
        type: 'number',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'purchase_price', 'number',
          (r, value) => {
            let price = value;
            if (!price && Array.isArray(r.coins)) {
              price = r.coins.reduce((sum, coin) => sum + (Number(coin.purchase_price) || 0), 0);
            }
            return `$${price.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
          }
        )
      }
    ];

    // Add contact and notes columns
    const finalColumns = [
      ...baseColumns,
      ...typeSpecificColumns,
      {
        field: 'contact_name',
        headerName: 'Contact',
        type: 'string',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'contact_name', 'string',
          (r, value) => r.contact_id ? (
            <Link to={`/contacts/${r.contact_id}`} className="text-blue-600 hover:text-blue-800">
              {value}
            </Link>
          ) : (
            <span className="text-gray-500">No contact assigned</span>
          )
        )
      },
      {
        field: 'notes',
        headerName: 'Notes',
        type: 'string',
        sortable: true,
        renderCell: (row) => renderCellSafely(row, 'notes', 'string',
          (r, value) => value
        )
      }
    ];

    // Apply column order if specified
    if (columnOrder.length > 0) {
      const orderMap = new Map(columnOrder.map((field, index) => [field, index]));
      finalColumns.sort((a, b) => {
        const aIndex = orderMap.has(a.field) ? orderMap.get(a.field) : Number.MAX_SAFE_INTEGER;
        const bIndex = orderMap.has(b.field) ? orderMap.get(b.field) : Number.MAX_SAFE_INTEGER;
        return aIndex - bIndex;
      });
    }

    return finalColumns;
  }, [transactions, type, columnOrder]);
};
